'use client';

import {
  Aquarius as AquariusIcon,
  Aries as AriesIcon,
  Cancer as CancerIcon,
  Capricorn as CapricornIcon,
  Gemini as GeminiIcon,
  Jupiter as JupiterIcon,
  Leo as LeoIcon,
  Libra as LibraIcon,
  Mercury as MercuryIcon,
  Pisces as PiscesIcon,
  Sagittarius as SagittariusIcon,
  Scorpio as ScorpioIcon,
  Sun as SunIcon,
  Taurus as TaurusIcon,
  Venus as VenusIcon,
  Virgo as VirgoIcon,
} from '@repo/lib/icons';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ChevronDown, Moon, Orbit, Star, Sun } from 'lucide-react';
import { useLocale } from 'next-intl';
import { useTheme } from 'next-themes';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { OnboardingData } from '@/features/onboarding/onboarding';
import OnboardingMockup from '@/features/onboarding/onboarding';
import styles from './landing.module.css';

const ORBIT_CENTER_X = 230;
const ORBIT_CENTER_Y = 182;
const ORBIT_ZODIAC_RADIUS = 137;
const ORBIT_ZODIAC_SEGMENTS = 6;
const ORBIT_HALF_TURN = 2;
const ORBIT_SCROLL_START = 0;
const ORBIT_SCROLL_END = 0.42;
const ORBIT_SCROLL_DISTANCE = 76;
const ORBIT_LABEL_OFFSET = 5;
const ORBIT_SCROLL_PROGRESS = [ORBIT_SCROLL_START, ORBIT_SCROLL_END];
const ORBIT_SCROLL_OFFSET = [ORBIT_SCROLL_START, ORBIT_SCROLL_DISTANCE];
const LENS_REVEAL_DELAY = 0.1;
const LENS_REVEAL_DURATION = 0.5;

type LandingExperienceProps = {
  onAccountContinue?: (data: OnboardingData) => void;
  onExistingAccount?: () => void;
  acquisitionIntent?: 'love' | 'decisions';
};
export const LANDING_COPY = {
  pt: {
    eyebrow: 'O ORÁCULO DE IA QUE CONHECE SEU MAPA',
    title: 'Pergunte ao céu.',
    titleEm: 'Receba uma resposta só sua.',
    lead: 'Não é um horóscopo. É um oráculo que lê seu mapa natal e responde à sua pergunta real.',
    login: 'Entrar',
    start: 'Começar grátis',
    cta: 'Fazer minha primeira pergunta',
    handoffEyebrow: 'SUA PRIMEIRA LEITURA É GRATUITA',
    handoffTitle: 'Insira seus dados. Pergunte qualquer coisa ao Oracle.',
    handoffBody:
      'Sem conta, sem cartão. Leva cerca de um minuto. O Oracle lê seu mapa e responde à sua pergunta — depois você decide se quer continuar.',
    startNow: 'Começar agora',
    trustStats: [
      '10+ posições planetárias lidas',
      'perguntas ilimitadas',
      'teste grátis de 7 dias, sem cartão',
    ],
    heroFine: 'Grátis · Sem conta · 60 segundos',
    testimonialQuote:
      'Perguntei ao Oráculo se devia aceitar a proposta de trabalho. Ele referenciou meu retorno de Saturno, verificou os trânsitos e disse para esperar seis semanas. Esperei. A proposta melhorou.',
    testimonialName: 'Carla A.',
    testimonialTag: 'Membro Cosmic Navigator',
    problemEyebrow: 'SOA FAMILIAR?',
    problemTitlePrefix: 'Apps de horóscopo',
    problemTitleEm: 'não respondem sua pergunta.',
    problemBody:
      'Você tem uma pergunta real sobre sua vida. Eles te dão um parágrafo escrito para 500 milhões de pessoas. Aqui está o que está quebrado — e o que fazemos diferente.',
    howEyebrow: 'COMO FUNCIONA',
    howTitlePrefix: 'Seu mapa, um guia de IA,',
    howTitle: 'Seu mapa, um guia de IA, e a pergunta na sua mente.',
    howTitleEm: 'e a pergunta na sua mente.',
    howBody:
      'Insira seus dados. A IA lê seu mapa — planetas, casas, aspectos — e responde à sua pergunta específica. Não um parágrafo para seu signo. Uma leitura para você.',
    featuresEyebrow: 'MAIS QUE UM ORÁCULO',
    featuresTitlePrefix: 'Leituras de tarô e mapa,',
    featuresTitleEm: 'integradas.',
    featuresBody:
      'O Oracle é o centro. Mas seu mapa também alimenta todo o resto — tiragens de tarô interpretadas através das suas posições, e um mapa natal que você consegue entender.',
    orbitEyebrow: 'SEU MAPA',
    orbitTitle: 'Seu mapa',
    orbitSubtitle: '10 posições · lido por IA',
    oracleDemoEyebrow: 'PERGUNTE. RECEBA.',
    oracleDemoTitlePrefix: 'Perguntas reais.',
    oracleDemoTitleEm: 'Respostas reais baseadas no mapa.',
    oracleDemoBody: 'parágrafos. Sem previsões genéricas. Só seu céu, lido.',
    existingAccount: 'Já tenho uma conta',
  },
  en: {
    eyebrow: 'THE AI ORACLE THAT KNOWS YOUR CHART',
    title: 'Ask the sky.',
    titleEm: "Get an answer that's yours.",
    lead: 'Not a horoscope. An AI oracle that reads your full birth chart and answers your actual question.',
    login: 'Log in',
    start: 'Start free',
    cta: 'Ask my first question',
    trustStats: [
      '10+ planetary placements read',
      'unlimited follow-up questions',
      '7-day free trial, no card',
    ],
    heroFine: 'Free · No account · 60 seconds',
    testimonialQuote:
      'I asked the Oracle if I should take the job offer. It referenced my Saturn return, checked the transit, and told me to wait six weeks. I waited. The offer got better.',
    testimonialName: 'Carla A.',
    testimonialTag: 'Cosmic Navigator member',
    problemEyebrow: 'SOUND FAMILIAR?',
    problemTitlePrefix: 'Horoscope apps',
    problemBody:
      "You have a real question about your life. They give you a paragraph written for 500 million people. Here's what's broken — and what we do instead.",
    problemTitleEm: "don't answer your question.",
    howTitle: 'Your chart, an AI guide, and the question on your mind.',
    howTitlePrefix: 'Your chart, an AI guide,',
    howTitleEm: 'and the question on your mind.',
    howBody:
      'Enter your birth data. The AI reads your chart — planets, houses, aspects — and answers your specific question. Not a paragraph for your sign. A reading for you.',
    howEyebrow: 'HOW IT WORKS',
    featuresTitleEm: 'built in.',
    featuresTitlePrefix: 'Tarot and chart readings,',
    featuresBody:
      'The Oracle is the center. But your chart also feeds everything else — tarot pulls interpreted through your placements, and a natal chart you can actually understand.',
    featuresEyebrow: 'MORE THAN AN ORACLE',
    orbitEyebrow: 'YOUR CHART',
    orbitTitle: 'Your chart',
    orbitSubtitle: '10 placements · read by AI',
    oracleDemoTitle: 'Real questions. Real chart-based answers.',
    oracleDemoTitlePrefix: 'Real questions.',
    oracleDemoTitleEm: 'Real chart-based answers.',
    oracleDemoBody: 'paragraphs. No generic predictions. Just your sky, read.',
    oracleDemoEyebrow: 'ASK. GET ANSWERS.',
    handoffEyebrow: 'YOUR FIRST READING IS FREE',
    handoffTitle: 'Enter your details. Ask the Oracle anything.',
    handoffBody:
      "No account, no credit card. Takes about a minute. The Oracle reads your chart and answers your question — then you decide if it's worth keeping.",
    startNow: 'Start now',
    existingAccount: 'I already have an account',
  },
  es: {
    eyebrow: 'EL ORÁCULO DE IA QUE CONOCE TU CARTA',
    title: 'Pregunta al cielo.',
    titleEm: 'Recibe una respuesta solo tuya.',
    lead: 'No es un horóscopo. Es un oráculo que lee tu carta natal y responde a tu pregunta real.',
    login: 'Entrar',
    start: 'Empezar gratis',
    cta: 'Hacer mi primera pregunta',
    handoffEyebrow: 'TU PRIMERA LECTURA ES GRATIS',
    handoffTitle: 'Añade tus datos. Pregunta lo que quieras al Oráculo.',
    handoffBody:
      'Sin cuenta, sin tarjeta. Tarda alrededor de un minuto. El Oráculo lee tu carta y responde a tu pregunta — después decides si quieres continuar.',
    trustStats: [
      '10+ posiciones planetarias leídas',
      'preguntas ilimitadas',
      'prueba gratis de 7 días, sin tarjeta',
    ],
    heroFine: 'Gratis · Sin cuenta · 60 segundos',
    testimonialQuote:
      'Le pregunté al Oráculo si debía aceptar la oferta de trabajo. Referenció mi retorno de Saturno, revisó los tránsitos y me dijo que esperara seis semanas. Esperé. La oferta mejoró.',
    testimonialName: 'Carla A.',
    testimonialTag: 'Miembro Cosmic Navigator',
    problemTitlePrefix: 'Las apps de horóscopo',
    problemBody:
      'Tienes una pregunta real sobre tu vida. Te dan un párrafo escrito para 500 millones de personas. Esto es lo que está roto — y lo que hacemos diferente.',
    howEyebrow: 'CÓMO FUNCIONA',
    howTitle: 'Tu carta, un guía de IA, y la pregunta en tu mente.',
    howTitlePrefix: 'Tu carta, un guía de IA,',
    howTitleEm: 'y la pregunta en tu mente.',
    howBody:
      'Introduce tus datos. La IA lee tu carta — planetas, casas, aspectos — y responde a tu pregunta específica. No un párrafo para tu signo. Una lectura para ti.',
    featuresTitle: 'Lecturas de tarot y carta, integradas.',
    featuresTitlePrefix: 'Lecturas de tarot y carta,',
    featuresTitleEm: 'integradas.',
    featuresBody:
      'El Oráculo es el centro. Pero tu carta también alimenta todo lo demás — tiradas de tarot interpretadas a través de tus posiciones, y una carta natal que puedes entender.',
    orbitTitle: 'Tu carta',
    orbitSubtitle: '10 posiciones · leído por IA',
    oracleDemoEyebrow: 'PREGUNTA. RECIBE.',
    oracleDemoTitle: 'Preguntas reales. Respuestas reales basadas en tu carta.',
    oracleDemoTitlePrefix: 'Preguntas reales.',
    oracleDemoTitleEm: 'Respuestas reales basadas en tu carta.',
    oracleDemoBody: 'párrafos. Sin predicciones genéricas. Solo tu cielo, leído.',
    startNow: 'Empezar ahora',
  },
} as const;

// ── Oracle-first positioning ──────────────────────
// The hero AI that knows your chart and helps you decide.

type LandingLocale = 'pt' | 'en' | 'es';
type LandingCopy = (typeof LANDING_COPY)['pt'];

const ORACLE_EXAMPLES: Record<LandingLocale, Array<{ q: string; a: string }>> = {
  pt: [
    {
      q: 'Será que eu mando mensagem pra ele?',
      a: 'Seu Marte em Câncer quer proteger, mas Mercúrio em Áries quer respostas. Espere 48 horas — Vênus entra em Leão na quinta, e a conversa vai soar diferente.',
    },
    {
      q: 'Por que eu me saboto no trabalho?',
      a: 'Seu Saturno na casa 10 construiu um muro entre ambição e visibilidade. A carta A Torre confirma: a estratégia antiga precisa cair antes da nova se firmar.',
    },
    {
      q: 'Esse relacionamento vale a pena lutar por ele?',
      a: 'Sua Lua em Escorpião precisa de profundidade emocional. A Vênus dele em Gêmeos passa na superfície. A sinastria mostra 62% de atrito em intimidade. Decida se a lacuna é transponível.',
    },
  ],
  en: [
    {
      q: 'Should I text him back?',
      a: 'Your Mars in Cancer wants to protect, but Mercury in Aries wants answers. Wait 48 hours — Venus enters Leo on Thursday, and the conversation will land differently.',
    },
    {
      q: 'Why do I keep self-sabotaging at work?',
      a: 'Your Saturn in the 10th house built a wall between ambition and visibility. The Tower card confirms: the old strategy needs to fall before the new one holds.',
    },
    {
      q: 'Is this relationship worth fighting for?',
      a: 'Your Moon in Scorpio needs emotional depth. His Venus in Gemini skims the surface. The synastry shows a 62% friction score on intimacy. Decide if the gap is bridgeable.',
    },
  ],
  es: [
    {
      q: '¿Debería escribirle de nuevo?',
      a: 'Tu Marte en Cáncer quiere proteger, pero Mercurio en Aries quiere respuestas. Espera 48 horas — Venus entra en Leo el jueves, y la conversación sonará diferente.',
    },
    {
      q: '¿Por qué me saboteo en el trabajo?',
      a: 'Tu Saturno en la casa 10 construyó un muro entre ambición y visibilidad. La carta La Torre confirma: la estrategia antigua debe caer antes de que la nueva se afirme.',
    },
    {
      q: '¿Vale la pena luchar por esta relación?',
      a: 'Tu Luna en Escorpio necesita profundidad emocional. Su Venus en Géminis roza la superficie. La sinastria muestra 62% de fricción en intimidad. Decide si la brecha es salvable.',
    },
  ],
};

const PROBLEM_POINTS: Record<
  LandingLocale,
  Array<{ glyph: string; title: string; copy: string }>
> = {
  pt: [
    {
      glyph: '♻',
      title: 'Apps de horóscopo reciclam o mesmo parágrafo',
      copy: 'Seu signo solar é um doze avos do seu mapa. Um app que para aí não consegue distinguir sua história da de mais ninguém — e toda resposta parece escrita para outra pessoa.',
    },
    {
      glyph: '◇',
      title: 'Você tem perguntas. Eles têm previsões genéricas.',
      copy: '"Mercúrio retrógrado pode causar má comunicação." Você já sabia disso. Queria saber o que isso significa para SEU trabalho, SEU relacionamento, SUA decisão — não para 500 milhões de outras pessoas.',
    },
    {
      glyph: '✕',
      title: 'Apps de astrologia não respondem',
      copy: 'Você lê um parágrafo e fecha o app. Sem pergunta de acompanhamento. Sem "mas e sobre minha situação?" Você fica interpretando sozinho — ou pior, pesquisando e achando uma listinha.',
    },
  ],
  en: [
    {
      glyph: '♻',
      title: 'Horoscope apps recycle the same paragraph',
      copy: "Your sun sign is one-twelfth of your chart. An app that stops there can't tell your story from anyone else's — and every answer feels like it was written for someone else.",
    },
    {
      glyph: '◇',
      title: 'You have questions. They have generic predictions.',
      copy: '"Mercury retrograde might cause miscommunication." You already knew that. You wanted to know what it means for YOUR job, YOUR relationship, YOUR decision — not 500 million other people\'s.',
    },
    {
      glyph: '✕',
      title: "Astrology apps don't talk back",
      copy: 'You read a paragraph and close the app. No follow-up question. No "but what about my situation?" You\'re left to interpret alone — or worse, Google it and get a listicle.',
    },
  ],
  es: [
    {
      glyph: '♻',
      title: 'Las apps de horóscopo reciclan el mismo párrafo',
      copy: 'Tu signo solar es un doceavo de tu carta. Una app que se detiene ahí no puede distinguir tu historia de la de nadie más — y cada respuesta parece escrita para otra persona.',
    },
    {
      glyph: '◇',
      title: 'Tienes preguntas. Ellos tienen predicciones genéricas.',
      copy: '"Mercurio retrógrado puede causar mala comunicación." Ya lo sabías. Querías saber qué significa para TU trabajo, TU relación, TU decisión — no para 500 millones de personas más.',
    },
    {
      glyph: '✕',
      title: 'Las apps de astrología no responden',
      copy: 'Lees un párrafo y cierras la app. Sin pregunta de seguimiento. Sin "¿pero qué pasa con mi situación?" Te quedas interpretando solo — o peor, buscas en Google y encuentras una lista.',
    },
  ],
};

const MECHANISM_STEPS: Record<
  LandingLocale,
  Array<{ num: string; title: string; copy: string }>
> = {
  pt: [
    {
      num: '01',
      title: 'Seu mapa completo, mapeado',
      copy: 'Data, hora e local de nascimento. Calculamos todas as dez posições planetárias — não apenas seu signo solar. Seu mapa é a base sobre a qual tudo se constrói.',
    },
    {
      num: '02',
      title: 'Pergunte qualquer coisa. Receba uma resposta real.',
      copy: 'Digite sua pergunta. O Oracle lê seu mapa, verifica os trânsitos de hoje contra suas posições, e responde em linguagem que você pode usar. Nada de "esta semana será desafiadora." Específico. Pessoal. Útil.',
    },
    {
      num: '03',
      title: 'Continue perguntando quantas vezes precisar',
      copy: '"Mas e meu trabalho?" — pergunte. "E meu ex?" — pergunte. O Oracle lembra seu mapa e sua conversa. Não é um gerador de horóscopo. É um guia que conhece seu céu.',
    },
  ],
  en: [
    {
      num: '01',
      title: 'Your full chart, mapped',
      copy: 'Date, time, and place of birth. We calculate all ten planetary placements — not just your sun sign. Your chart is the foundation everything else builds on.',
    },
    {
      num: '02',
      title: 'Ask anything. Get a real answer.',
      copy: 'Type your question. The Oracle reads your chart, checks today\'s transits against your placements, and responds in language you can actually use. No "this week will be challenging." Specific. Personal. Useful.',
    },
    {
      num: '03',
      title: 'Follow up as many times as you need',
      copy: '"But what about my job?" — ask. "And my ex?" — ask. The Oracle remembers your chart and your conversation. It\'s not a horoscope generator. It\'s a guide who knows your sky.',
    },
  ],
  es: [
    {
      num: '01',
      title: 'Tu carta completa, mapeada',
      copy: 'Fecha, hora y lugar de nacimiento. Calculamos las diez posiciones planetarias — no solo tu signo solar. Tu carta es la base sobre la que todo se construye.',
    },
    {
      num: '02',
      title: 'Pregunta lo que quieras. Recibe una respuesta real.',
      copy: 'Escribe tu pregunta. El Oráculo lee tu carta, verifica los tránsitos de hoy contra tus posiciones, y responde en un lenguaje que puedes usar. Nada de "esta semana será un reto." Específico. Personal. Útil.',
    },
    {
      num: '03',
      title: 'Sigue preguntando las veces que necesites',
      copy: '"¿Y mi trabajo?" — pregunta. "¿Y mi ex?" — pregunta. El Oráculo recuerda tu carta y tu conversación. No es un generador de horóscopos. Es un guía que conoce tu cielo.',
    },
  ],
};

const FEATURES: Record<
  LandingLocale,
  Array<{ label: string; title: string; copy: string; tone: string }>
> = {
  pt: [
    {
      label: 'ORÁCULO DE IA',
      title: 'Pergunte. Receba respostas que se encaixam no seu mapa.',
      copy: 'A única IA que lê seu mapa natal completo antes de responder. Cada resposta referencia suas posições reais e os trânsitos de hoje — não um parágrafo reciclado.',
      tone: 'violet',
    },
    {
      label: 'TARÔ INTEGRADO',
      title: 'Tire cartas. Receba uma leitura que é sua.',
      copy: 'Três tiragens — diária, três cartas e Cruz Céltica. Cada carta é interpretada através do seu mapa, não puxada de um banco de significados genéricos.',
      tone: 'blue',
    },
    {
      label: 'MAPA, SEM COMPLICAÇÃO',
      title: 'Seu mapa, explicado uma peça de cada vez.',
      copy: 'Clique em qualquer planeta, casa ou aspecto. Receba uma explicação em linguagem simples do que significa para você — sem diploma em astrologia. Pergunte ao Oracle sobre qualquer parte que não entende.',
      tone: 'pink',
    },
  ],
  en: [
    {
      label: 'AI ORACLE',
      title: 'Ask. Get answers that fit your chart.',
      copy: "The only AI that reads your full natal chart before responding. Every answer references your actual placements and today's transits — not a recycled paragraph.",
      tone: 'violet',
    },
    {
      label: 'TAROT, BUILT IN',
      title: "Pull cards. Get a reading that's yours.",
      copy: 'Three spreads — daily, three-card, and Celtic Cross. Every card is interpreted through your chart, not pulled from a database of generic meanings.',
      tone: 'blue',
    },
    {
      label: 'CHART, UNCOMPLICATED',
      title: 'Your map, explained one piece at a time.',
      copy: "Click any planet, house, or aspect. Get a plain-language explanation of what it means for you — no astrology degree required. Ask the Oracle about any part you don't understand.",
      tone: 'pink',
    },
  ],
  es: [
    {
      label: 'ORÁCULO DE IA',
      title: 'Pregunta. Recibe respuestas que se ajustan a tu carta.',
      copy: 'La única IA que lee tu carta natal completa antes de responder. Cada respuesta referencia tus posiciones reales y los tránsitos de hoy — no un párrafo reciclado.',
      tone: 'violet',
    },
    {
      label: 'TAROT INTEGRADO',
      title: 'Saca cartas. Recibe una lectura tuya.',
      copy: 'Tres tiradas — diaria, tres cartas y Cruz Céltica. Cada carta se interpreta a través de tu carta, no extraída de una base de significados genéricos.',
      tone: 'blue',
    },
    {
      label: 'CARTA, SIN COMPLICACIÓN',
      title: 'Tu mapa, explicado pieza por pieza.',
      copy: 'Haz clic en cualquier planeta, casa o aspecto. Recibe una explicación en lenguaje sencillo de lo que significa para ti — sin título en astrología. Pregunta al Oráculo sobre cualquier parte que no entiendas.',
      tone: 'pink',
    },
  ],
};

const LANDING_ZODIAC_ICONS = [
  { name: 'Aries', Icon: AriesIcon },
  { name: 'Taurus', Icon: TaurusIcon },
  { name: 'Gemini', Icon: GeminiIcon },
  { name: 'Cancer', Icon: CancerIcon },
  { name: 'Leo', Icon: LeoIcon },
  { name: 'Virgo', Icon: VirgoIcon },
  { name: 'Libra', Icon: LibraIcon },
  { name: 'Scorpio', Icon: ScorpioIcon },
  { name: 'Sagittarius', Icon: SagittariusIcon },
  { name: 'Capricorn', Icon: CapricornIcon },
  { name: 'Aquarius', Icon: AquariusIcon },
  { name: 'Pisces', Icon: PiscesIcon },
] as const;

function OrbitPreview({ locale }: { locale: LandingLocale }) {
  const { scrollYProgress } = useScroll();
  const orbitY = useTransform(scrollYProgress, ORBIT_SCROLL_PROGRESS, ORBIT_SCROLL_OFFSET);
  const orbitCopy = LANDING_COPY[locale] as LandingCopy;

  return (
    <motion.div className={styles.orbitFrame} style={{ y: orbitY }}>
      <div className={styles.orbitTopline}>
        <span>
          <strong>{orbitCopy.orbitTitle}</strong>
          <small>{orbitCopy.orbitSubtitle}</small>
        </span>
        <span className={styles.liveState}>
          <i /> {locale === 'pt' ? 'céu ao vivo' : locale === 'es' ? 'cielo en vivo' : 'live sky'}
        </span>
      </div>
      <svg
        aria-labelledby="landing-orbit-title landing-orbit-desc"
        className={styles.orbitSvg}
        role="img"
        viewBox="0 0 460 360"
      >
        <title id="landing-orbit-title">A natal chart-inspired orbit map</title>
        <desc id="landing-orbit-desc">
          Zodiac signs and planetary points arranged around a luminous center.
        </desc>
        <defs>
          <radialGradient cx="40%" cy="35%" id="landing-orbit-sun">
            <stop offset="0" stopColor="#fff0b7" />
            <stop offset="0.55" stopColor="#f3b6d5" />
            <stop offset="1" stopColor="#9a5ed2" />
          </radialGradient>
          <filter id="landing-orbit-shadow">
            <feGaussianBlur stdDeviation="10" />
          </filter>
        </defs>
        <circle className={styles.orbitOuter} cx="230" cy="182" r="151" />
        <circle className={styles.orbitRing} cx="230" cy="182" r="126" />
        <circle className={styles.orbitInner} cx="230" cy="182" r="80" />
        {LANDING_ZODIAC_ICONS.map(({ name, Icon }, index) => {
          const angle = (index * Math.PI) / ORBIT_ZODIAC_SEGMENTS - Math.PI / ORBIT_HALF_TURN;
          return (
            <Icon
              aria-label={name}
              className={styles.zodiacSign}
              height="16"
              key={name}
              width="16"
              x={ORBIT_CENTER_X + Math.cos(angle) * ORBIT_ZODIAC_RADIUS - 8}
              y={ORBIT_CENTER_Y + ORBIT_LABEL_OFFSET + Math.sin(angle) * ORBIT_ZODIAC_RADIUS - 8}
            />
          );
        })}
        <ellipse
          className={styles.orbitLine}
          cx="230"
          cy="182"
          rx="204"
          ry="86"
          transform="rotate(-18 230 182)"
        />
        <ellipse
          className={styles.orbitLine}
          cx="230"
          cy="182"
          rx="154"
          ry="61"
          transform="rotate(28 230 182)"
        />
        <ellipse
          className={styles.orbitLine}
          cx="230"
          cy="182"
          rx="103"
          ry="42"
          transform="rotate(-42 230 182)"
        />
        <path
          className={styles.connection}
          d="M230 182 100 105M230 182 355 105M230 182 328 252M230 182 140 262"
        />
        <circle
          cx="230"
          cy="182"
          fill="currentColor"
          filter="url(#landing-orbit-shadow)"
          opacity="0.13"
          r="54"
        />
        <SunIcon
          aria-hidden="true"
          className={styles.sunIcon}
          height="30"
          width="30"
          x="215"
          y="167"
        />
        <g className={styles.planetPink} transform="translate(100 105)">
          <circle r="8" />
          <VenusIcon
            aria-hidden="true"
            className={styles.planetIcon}
            height="16"
            width="16"
            x="-8"
            y="-8"
          />
          <text className={styles.planetLabel} x="16" y="4">
            Venus
          </text>
        </g>
        <g className={styles.planetBlue} transform="translate(355 105)">
          <circle r="10" />
          <JupiterIcon
            aria-hidden="true"
            className={styles.planetIcon}
            height="18"
            width="18"
            x="-9"
            y="-9"
          />
          <text className={styles.planetLabel} x="16" y="4">
            Jupiter
          </text>
        </g>
        <g className={styles.planetGold} transform="translate(328 252)">
          <circle r="8" />
          <MercuryIcon
            aria-hidden="true"
            className={styles.planetIcon}
            height="16"
            width="16"
            x="-8"
            y="-8"
          />
          <text className={styles.planetLabel} x="16" y="4">
            Mercury
          </text>
        </g>
      </svg>
      <div className={styles.orbitLegend}>
        <span>
          <i className={styles.dotPink} /> personal planets
        </span>
        <span>
          <i className={styles.dotBlue} /> social planets
        </span>
        <span>
          <i className={styles.dotGold} /> current transit
        </span>
      </div>
    </motion.div>
  );
}

function OracleDemo({ locale }: { locale: LandingLocale }) {
  return (
    <div className={styles.oracleDemo}>
      <div className={styles.oracleChat}>
        {ORACLE_EXAMPLES[locale].map((ex, i) => (
          <div className={styles.oracleExchange} key={ex.q}>
            <div className={styles.oracleBubbleUser}>
              <span>{ex.q}</span>
            </div>
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className={styles.oracleBubbleAI}
              initial={{ opacity: 0, y: 8 }}
              transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
            >
              <span className={styles.oracleAIBadge}>
                <Orbit aria-hidden="true" size={12} />
                Oracle
              </span>
              <p>{ex.a}</p>
            </motion.div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LandingExperience({
  onAccountContinue,
  onExistingAccount,
  acquisitionIntent,
}: LandingExperienceProps) {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  const localeValue = useLocale();
  const locale = localeValue === 'pt' || localeValue === 'es' ? localeValue : 'en';
  const copy = LANDING_COPY[locale] as LandingCopy;

  const onboardingRef = useRef<HTMLElement>(null);
  const hasSnapped = useRef(false);
  const scrollToOnboarding = useCallback(() => {
    onboardingRef.current?.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      block: 'start',
    });
  }, []);

  useEffect(() => {
    const target = onboardingRef.current;
    if (!target) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (hasSnapped.current || !entry.isIntersecting || entry.boundingClientRect.top <= 0) {
          return;
        }
        hasSnapped.current = true;
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
            ? 'auto'
            : 'smooth',
          block: 'start',
        });
      },
      { threshold: 0.01 }
    );
    observer.observe(target);

    return () => observer.disconnect();
  }, []);

  return (
    <main className={styles.shell}>
      <div aria-hidden="true" className={styles.ambient} />
      <nav aria-label="Main navigation" className={styles.nav}>
        <a className={styles.wordmark} href="#top">
          <span>
            STARDECIDE
            <small>
              {locale === 'pt'
                ? 'seu céu, lido por IA'
                : locale === 'es'
                  ? 'tu cielo, leído por IA'
                  : 'your sky, read by AI'}
            </small>
          </span>
        </a>
        <div className={styles.navActions}>
          <button
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
            className={styles.themeToggle}
            onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
            type="button"
          >
            {mounted ? (
              theme === 'light' ? (
                <Moon size={16} />
              ) : (
                <Sun size={16} />
              )
            ) : (
              <Moon aria-hidden="true" size={16} />
            )}
          </button>
          {onExistingAccount && (
            <button className={styles.navLogin} onClick={onExistingAccount} type="button">
              {copy.login}
            </button>
          )}
          <button className={styles.navCta} onClick={scrollToOnboarding} type="button">
            {copy.start} <ArrowRight aria-hidden="true" size={14} />
          </button>
        </div>
      </nav>

      {/* ── HERO: Oracle-first ─────────────────────── */}
      <section className={styles.hero} id="top">
        <div className={styles.heroCopy}>
          <motion.p
            animate={{ opacity: 1, y: 0 }}
            className={styles.eyebrow}
            initial={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.5 }}
          >
            {copy.eyebrow}
          </motion.p>
          <motion.h1
            animate={{ opacity: 1, y: 0 }}
            initial={{ opacity: 0, y: 18 }}
            transition={{ delay: 0.08, duration: 0.65 }}
          >
            {copy.title}
            <br />
            <em>{copy.titleEm}</em>
          </motion.h1>
          <motion.p
            animate={{ opacity: 1, y: 0 }}
            className={styles.heroLead}
            initial={{ opacity: 0, y: 18 }}
            transition={{ delay: 0.16, duration: 0.65 }}
          >
            {copy.lead}
          </motion.p>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className={styles.heroActions}
            initial={{ opacity: 0, y: 18 }}
            transition={{ delay: 0.24, duration: 0.65 }}
          >
            <button className={styles.primaryCta} onClick={scrollToOnboarding} type="button">
              {copy.cta} <ArrowRight aria-hidden="true" size={18} />
            </button>
            <span className={styles.heroFine}>{copy.heroFine}</span>
          </motion.div>
          <motion.div
            animate={{ opacity: 1, y: 0 }}
            className={styles.trustBand}
            initial={{ opacity: 0, y: 18 }}
            transition={{ delay: 0.32, duration: 0.65 }}
          >
            {copy.trustStats.map((stat) => (
              <div className={styles.trustStat} key={stat}>
                <strong className={styles.trustValue}>{stat.split(' ')[0]}</strong>
                <small className={styles.trustLabel}>{stat}</small>
              </div>
            ))}
          </motion.div>
        </div>
        <OrbitPreview locale={locale} />
      </section>

      {/* ── ORACLE DEMO: show, don't tell ─────────── */}
      <section className={styles.oracleDemoSection} id="demo">
        <motion.div
          className={styles.sectionIntro}
          initial={{ opacity: 0, y: 22 }}
          viewport={{ amount: 0.3, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className={styles.eyebrow}>{copy.oracleDemoEyebrow}</p>
          <h2>
            {copy.oracleDemoTitlePrefix} <em>{copy.oracleDemoTitleEm}</em>
          </h2>
          <p>{copy.oracleDemoBody}</p>
        </motion.div>
      </section>

      {/* ── PROBLEM ───────────────────────────────── */}
      <section className={styles.problem} id="problem">
        <motion.div
          className={styles.sectionIntro}
          initial={{ opacity: 0, y: 22 }}
          viewport={{ amount: 0.3, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className={styles.eyebrow}>{copy.problemEyebrow}</p>
          <h2>
            {copy.problemTitlePrefix} <em>{copy.problemTitleEm}</em>
          </h2>
          <p>{copy.problemBody}</p>
        </motion.div>
        <div className={styles.problemGrid}>
          {PROBLEM_POINTS[locale].map((point, index) => (
            <motion.article
              className={styles.problemCard}
              initial={{ opacity: 0, y: 24 }}
              key={point.title}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ amount: 0.3, once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <span className={styles.problemGlyph}>{point.glyph}</span>
              <div className={styles.problemContent}>
                <h3>{point.title}</h3>
                <p>{point.copy}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* ── MECHANISM: how it works ───────────────── */}
      <section className={styles.howItWorks} id="how">
        <motion.div
          className={styles.sectionIntro}
          initial={{ opacity: 0, y: 22 }}
          viewport={{ amount: 0.3, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className={styles.eyebrow}>{copy.howEyebrow}</p>
          <h2>
            {copy.howTitlePrefix} <em>{copy.howTitleEm}</em>
          </h2>
          <p>{copy.howBody}</p>
        </motion.div>
        <div className={styles.stepsGrid}>
          {MECHANISM_STEPS[locale].map((step, index) => (
            <motion.article
              className={styles.stepCard}
              initial={{ opacity: 0, y: 24 }}
              key={step.num}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              viewport={{ amount: 0.3, once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <span className={styles.stepNum}>{step.num}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </motion.article>
          ))}
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────── */}
      <section className={styles.story} id="lenses">
        <motion.div
          className={styles.sectionIntro}
          initial={{ opacity: 0, y: 22 }}
          viewport={{ amount: 0.3, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <p className={styles.eyebrow}>{copy.featuresEyebrow}</p>
          <h2>
            {copy.featuresTitlePrefix} <em>{copy.featuresTitleEm}</em>
          </h2>
          <p>{copy.featuresBody}</p>
        </motion.div>
        <div className={styles.lensGrid}>
          {FEATURES[locale].map(({ copy: featureCopy, label, title, tone }, index) => (
            <motion.article
              className={`${styles.lensCard} ${styles[tone]}`}
              initial={{ opacity: 0, y: 28 }}
              key={label}
              transition={{ delay: index * LENS_REVEAL_DELAY, duration: LENS_REVEAL_DURATION }}
              viewport={{ amount: 0.3, once: true }}
              whileInView={{ opacity: 1, y: 0 }}
            >
              <p className={styles.lensLabel}>{label}</p>
              <h3>{title}</h3>
              <p>{featureCopy}</p>
              <span className={styles.lensLine} />
            </motion.article>
          ))}
        </div>
      </section>

      {/* ── PROOF ─────────────────────────────────── */}
      <section className={styles.testimonial} id="proof">
        <motion.div
          className={styles.testimonialPanel}
          initial={{ opacity: 0, y: 28 }}
          viewport={{ amount: 0.3, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div className={styles.testimonialStars}>
            {[0, 1, 2, 3, 4].map((i) => (
              <Star aria-hidden="true" fill="currentColor" key={i} size={18} />
            ))}
          </div>
          <blockquote className={styles.testimonialQuote}>
            &ldquo;{copy.testimonialQuote}&rdquo;
          </blockquote>
          <div className={styles.testimonialAttribution}>
            <strong>{copy.testimonialName}</strong>
            <small>{copy.testimonialTag}</small>
          </div>
        </motion.div>
      </section>

      {/* ── HANDOFF CTA ───────────────────────────── */}
      <section className={styles.handoff}>
        <motion.div
          className={styles.handoffPanel}
          initial={{ opacity: 0, y: 28 }}
          viewport={{ amount: 0.35, once: true }}
          whileInView={{ opacity: 1, y: 0 }}
        >
          <div>
            <p className={styles.eyebrow}>{copy.handoffEyebrow}</p>
            <h2>{copy.handoffTitle}</h2>
            <p>{copy.handoffBody}</p>
          </div>
          <button className={styles.scrollCue} onClick={scrollToOnboarding} type="button">
            <span className={styles.scrollCueText}>{copy.startNow}</span>
            <motion.span
              animate={{ y: [0, 6, 0] }}
              aria-hidden="true"
              className={styles.scrollCueIcon}
              transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            >
              <ChevronDown size={22} />
            </motion.span>
          </button>
        </motion.div>
      </section>

      <section className={styles.onboarding} id="onboarding" ref={onboardingRef}>
        <OnboardingMockup
          onAccountContinue={onAccountContinue}
          onExistingAccount={onExistingAccount}
          existingAccountLabel={copy.existingAccount}
          startAtGoal
          initialGoal={
            acquisitionIntent === 'love'
              ? 'Navigate a relationship'
              : acquisitionIntent === 'decisions'
                ? 'Daily guidance & clarity'
                : undefined
          }
        />
      </section>
    </main>
  );
}
